import React from 'react';
import { Box, Paper, Typography, Divider, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

const MarketForecastChart = ({ forecast, forecastDrivers }) => {
  const { t } = useTranslation();
  if (!forecast || forecast.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper', boxShadow: 'none' }, transition: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('dashboard.forecast.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.forecast.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#667eea', borderRadius: 1 }} />
              <Typography variant="caption">{t('dashboard.forecast.expected')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#11998e', borderRadius: 1 }} />
              <Typography variant="caption">{t('dashboard.forecast.bull')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#ff6b6b', borderRadius: 1 }} />
              <Typography variant="caption">{t('dashboard.forecast.bear')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#ffa726', borderRadius: 1 }} />
              <Typography variant="caption">{t('dashboard.forecast.base')}</Typography>
            </Box>
          </Box>
        </Box>
        
        <ResponsiveContainer width="100%" height={400} minWidth={400} minHeight={400}>
          <AreaChart data={forecast}>
            <defs>
              <linearGradient id="confidenceBand" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#667eea" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#667eea" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              domain={[-20, 80]}
              tick={{ fontSize: 12 }}
              label={{ value: t('dashboard.forecast.netMacroScore'), angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600 } }}
            />
            <Tooltip 
              wrapperStyle={{ zIndex: 1000 }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <Box sx={{ 
                      bgcolor: 'white', 
                      p: 2, 
                      border: '2px solid',
                      borderColor: data.regime_color,
                      borderRadius: 2,
                      boxShadow: 3,
                      minWidth: 250,
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        {data.date} ({t('dashboard.forecast.month')} {data.month})
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{t('dashboard.forecast.expectedScore')}:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{data.expected_score}</Typography>
                      </Box>
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          {t('dashboard.forecast.confidenceBand')}:
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 1 }}>
                          <Typography variant="caption" color="text.secondary">{t('dashboard.forecast.lowerBand')}:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{data.lower_band}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 1 }}>
                          <Typography variant="caption" color="text.secondary">{t('dashboard.forecast.upperBand')}:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{data.upper_band}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{t('dashboard.forecast.bullScenario')}:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#11998e' }}>
                          {data.bull_scenario}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{t('dashboard.forecast.bearScenario')}:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#ff6b6b' }}>
                          {data.bear_scenario}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">{t('dashboard.forecast.baseScenario')}:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#ffa726' }}>
                          {data.base_scenario}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        mt: 1, 
                        p: 1, 
                        borderRadius: 1, 
                        bgcolor: alpha(data.regime_color, 0.1),
                        textAlign: 'center',
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: data.regime_color }}>
                          {t(data.regime_key)} • {t('dashboard.forecast.confidenceLabel')}: {data.confidence}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: 20 }}
              iconType="line"
            />
            <ReferenceLine y={10} stroke="#11998e" strokeDasharray="5 5" label={t('dashboard.forecast.regime.riskOn')} />
            <ReferenceLine y={-10} stroke="#ff6b6b" strokeDasharray="5 5" label={t('dashboard.forecast.regime.riskOff')} />
            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
            
            {/* Confidence Band */}
            <Area 
              type="monotone" 
              dataKey="upper_band" 
              stroke="none" 
              fill="url(#confidenceBand)" 
              fillOpacity={1}
              name={t('dashboard.forecast.upperBand')}
            />
            <Area 
              type="monotone" 
              dataKey="lower_band" 
              stroke="none" 
              fill="white" 
              fillOpacity={1}
              name={t('dashboard.forecast.lowerBand')}
            />
            
            {/* Scenario Lines */}
            <Line 
              type="monotone" 
              dataKey="bull_scenario" 
              stroke="#11998e" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name={t('dashboard.forecast.bullScenario')}
            />
            <Line 
              type="monotone" 
              dataKey="bear_scenario" 
              stroke="#ff6b6b" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name={t('dashboard.forecast.bearScenario')}
            />
            <Line 
              type="monotone" 
              dataKey="base_scenario" 
              stroke="#ffa726" 
              strokeWidth={2}
              strokeDasharray="3 3"
              dot={false}
              name={t('dashboard.forecast.baseScenario')}
            />
            
            {/* Expected Line */}
            <Line 
              type="monotone" 
              dataKey="expected_score" 
              stroke="#667eea" 
              strokeWidth={3}
              dot={{ r: 4, fill: '#667eea', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6 }}
              name={t('dashboard.forecast.expectedScore')}
            />
          </AreaChart>
        </ResponsiveContainer>
        
        {/* Forecast Drivers */}
        {forecastDrivers && forecastDrivers.drivers && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('dashboard.forecast.keyDrivers')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
              {forecastDrivers.drivers.map((driver, index) => {
                // Translate impact for color determination
                const impactTranslated = t(driver.impactKey);
                const isPositive = driver.impactKey.includes('positive');
                
                return (
                  <Box 
                    key={index}
                    sx={{ 
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: isPositive ? alpha('#11998e', 0.05) : alpha('#ff6b6b', 0.05),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(driver.factorKey)}
                      </Typography>
                      <Box sx={{ 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1, 
                        bgcolor: isPositive ? alpha('#11998e', 0.1) : alpha('#ff6b6b', 0.1),
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 600, 
                          color: isPositive ? '#11998e' : '#ff6b6b' 
                        }}>
                          {impactTranslated}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t(driver.descriptionKey, driver.descriptionParams)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MarketForecastChart;


